import Modal from '@src/components/common/Modal';
import PhotoModalTop from '@src/components/common/PhotoModalTop';
import {
  PICKER_MODAL_INSET,
  PickerModalCard,
} from '@src/components/common/pickerModalStyles';
import SelectedCheckBadge from '@src/components/common/SelectedCheckBadge';
import { useCompositionContext } from '@src/components/sidepanel/CompositionContext';
import { PhotoTab } from '@src/types.ts/story';
import { LogManager } from '@src/utility/Logger';
import { useState } from 'react';
import styled from 'styled-components';

import { useSaveUploadedMediaCard } from '../CompositionSettings/useSaveUploadedMediaCard';
import { isHookComposition } from './videoTemplateUtils';

import type { VideoTemplateElementType } from './VideoTemplatesContext';
import type { Artifact } from '@src/types.ts/story';

const logger = LogManager.getLogger('component:ReplaceCompositionModal');

/** Selection key for the card built from the creator's own upload. */
const UPLOADED_SELECTION = 'uploaded-media';

type UploadedMedia = {
  mediaType: 'image' | 'video';
  url: string;
  artifact: Artifact;
};

interface ReplaceElementModalProps {
  isOpen: boolean;
  onClose: () => void;
  elementType: VideoTemplateElementType;
  onReplace: (newTemplateId: string) => void;
  isAdding?: boolean;
}

export const ReplaceElementModal = ({
  isOpen,
  onClose,
  elementType,
  onReplace,
  isAdding = false,
}: ReplaceElementModalProps) => {
  const { compositions } = useCompositionContext();
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(
    null,
  );
  // PLAT-11732 — an intro / end card can be the creator's own image or video
  const canUploadMedia =
    elementType === 'introCard' || elementType === 'endCard';
  const [uploadedMedia, setUploadedMedia] = useState<UploadedMedia | null>(
    null,
  );
  const [isPreparingUpload, setIsPreparingUpload] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const saveUploadedMediaCard = useSaveUploadedMediaCard();

  const handleMediaUploaded = (artifact: Artifact) => {
    setUploadedMedia({
      mediaType: artifact.responsiveImage ? 'image' : 'video',
      url: artifact.url,
      artifact,
    });
    setUploadError(null);
    setSelectedTemplateId(UPLOADED_SELECTION);
  };

  const matchingCompositions = compositions.filter(composition =>
    // PLAT-11034 — a hook is not its own composition type: it is an
    // OVERLAY on a clip-bound placement, so the hook slot filters on
    // what a hook actually is. Every other slot matches its type.
    elementType === 'hook'
      ? isHookComposition(composition)
      : composition.compositionType === elementType,
  );

  const handleReplace = async () => {
    if (selectedTemplateId === UPLOADED_SELECTION) {
      if (!uploadedMedia || !canUploadMedia) return;
      setIsPreparingUpload(true);
      // also saved as a workspace template, so it is offered next time
      const result = await saveUploadedMediaCard({
        artifact: uploadedMedia.artifact,
        cardType: elementType,
      });
      setIsPreparingUpload(false);
      if (!result.ok) {
        logger.warn('Could not build a card from the upload', {
          url: uploadedMedia.url,
          error: result.error,
        });
        setUploadError("Couldn't read this video's length. Try another file.");
        return;
      }
      onReplace(result.value.cardId);
      onClose();
      return;
    }
    if (selectedTemplateId) {
      onReplace(selectedTemplateId);
      onClose();
    }
  };

  const getElementLabel = () => {
    switch (elementType) {
      case 'introCard':
        return 'intro card';
      case 'endCard':
        return 'end card';
      case 'nameCard':
        return 'name card';
      case 'hook':
        return 'hook';
      default:
        return 'element';
    }
  };

  const getModalTitle = () => {
    const action = isAdding ? 'Add' : 'Replace';
    switch (elementType) {
      case 'introCard':
        return `${action} Intro Card`;
      case 'endCard':
        return `${action} End Card`;
      case 'nameCard':
        return `${action} Name Card`;
      case 'hook':
        return `${action} Hook`;
      default:
        return `${action} Element`;
    }
  };

  return (
    <Modal isOpen={isOpen} closeModal={onClose}>
      <PickerModalCard>
        <PhotoModalTop
          title={getModalTitle()}
          isSelected={selectedTemplateId !== null}
          isLoading={isPreparingUpload}
          replaceAction={handleReplace}
          tab={PhotoTab.ai}
          isAdding={isAdding}
          customUpload={
            canUploadMedia
              ? {
                  label: 'Upload Image or Video',
                  acceptType: 'all',
                  onUploadComplete: handleMediaUploaded,
                }
              : undefined
          }
        />

        {uploadError && <UploadError role="alert">{uploadError}</UploadError>}

        {matchingCompositions.length || uploadedMedia ? (
          <CompositionGrid>
            {uploadedMedia && (
              <CompositionCard
                type="button"
                aria-pressed={selectedTemplateId === UPLOADED_SELECTION}
                onClick={() => setSelectedTemplateId(UPLOADED_SELECTION)}
              >
                <Thumb selected={selectedTemplateId === UPLOADED_SELECTION}>
                  {selectedTemplateId === UPLOADED_SELECTION && (
                    <SelectedCheckBadge />
                  )}
                  {uploadedMedia.mediaType === 'image' ? (
                    <ThumbnailImage src={uploadedMedia.url} alt="" />
                  ) : (
                    <ThumbnailVideo src={uploadedMedia.url} muted />
                  )}
                </Thumb>
                <CompositionLabel>
                  {uploadedMedia.mediaType === 'image'
                    ? 'Uploaded Image'
                    : 'Uploaded Video'}
                </CompositionLabel>
              </CompositionCard>
            )}
            {matchingCompositions.map(composition => (
              <CompositionCard
                key={composition.id}
                type="button"
                aria-pressed={selectedTemplateId === composition.id}
                onClick={() => setSelectedTemplateId(composition.id)}
              >
                <Thumb selected={selectedTemplateId === composition.id}>
                  {selectedTemplateId === composition.id && (
                    <SelectedCheckBadge />
                  )}
                  {composition.thumbnail?.url ? (
                    <ThumbnailImage
                      src={composition.thumbnail.url}
                      alt={composition.label}
                      loading="lazy"
                    />
                  ) : (
                    <PlaceholderContent>{composition.label}</PlaceholderContent>
                  )}
                </Thumb>
                <CompositionLabel>{composition.label}</CompositionLabel>
              </CompositionCard>
            ))}
          </CompositionGrid>
        ) : (
          <EmptyState>
            {canUploadMedia
              ? `No ${getElementLabel()} designs are available yet — upload an image or video to get started.`
              : `No ${getElementLabel()} designs are available yet.`}
          </EmptyState>
        )}
      </PickerModalCard>
    </Modal>
  );
};

/* Empty state per docs/design-system.md → "Empty States". */
const EmptyState = styled.div`
  min-height: 180px;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 0 ${PICKER_MODAL_INSET};
  color: var(--text-muted);
  font-size: var(--text-sm);
  line-height: var(--leading-text-sm);
  /* the upload hint makes it two lines; balance them so none is orphaned */
  text-wrap: balance;
`;

const CompositionGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 16px;
  max-height: 400px;
  overflow-y: auto;
  padding: 0 ${PICKER_MODAL_INSET};
`;

const CompositionCard = styled.button`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 0;
  background: transparent;
  border: none;
  cursor: pointer;
  text-align: left;
`;

/* Card frame + selection anchor, matching the picker cards in PhotosModal and
   OverlayGraphicPickerModal: neutral at rest, --primary on hover and when
   selected, with the gold check carrying the selection itself. */
const Thumb = styled.div<{ selected: boolean }>`
  position: relative;
  width: 100%;
  aspect-ratio: 16 / 9;
  box-sizing: border-box;
  overflow: hidden;
  border-radius: var(--radius-input);
  border: 1px solid
    ${props => (props.selected ? 'var(--primary)' : 'var(--border)')};
  transition: border-color var(--transition-fast) ease;

  ${CompositionCard}:hover & {
    border-color: var(--primary);
  }
`;

const ThumbnailImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`;

const ThumbnailVideo = styled.video`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`;

const UploadError = styled.div`
  padding: 0 ${PICKER_MODAL_INSET};
  margin-bottom: var(--spacing-md);
  color: var(--destructive);
  font-size: var(--text-sm);
  line-height: var(--leading-text-sm);
`;

const PlaceholderContent = styled.div`
  width: 100%;
  height: 100%;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--surface);
  color: var(--text-muted);
  font-size: var(--text-xs);
  text-align: center;
  padding: 8px;
`;

const CompositionLabel = styled.div`
  color: var(--text-secondary);
  font-family: var(--font-primary);
  font-size: var(--text-xs);
  text-align: center;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;
